import React from 'react';
import { Controller } from 'react-hook-form';
import HookFormUtils from './HookFormUtils';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Box, FormControl, InputLabel, MenuItem, Select, Stack, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  is12HourClinicTimeFormat,
  resolveClinicTimeFormat,
} from '../../services/ClinicTimeFormatUtils';
import useCurrentClinicTimeFormat from '../../hooks/useCurrentClinicTimeFormat';

export default function HookFormDateTimePicker({
  control,
  schema,
  variant,
  type = 'text',
  loading,
  errors,
  label,
  prefix = null,
  timeFormat,
  ...others
}) {
  const error = HookFormUtils.getFieldError(errors, schema, prefix);
  const _schema = HookFormUtils.getSchemaName(schema, prefix);
  const currentClinicTimeFormat = useCurrentClinicTimeFormat();

  return (
    <Controller
      control={control}
      name={_schema}
      render={({ field: { onChange, value } }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {(() => {
            const normalizedTimeFormat = resolveClinicTimeFormat(
              timeFormat || currentClinicTimeFormat
            );
            const use12HourFormat = is12HourClinicTimeFormat(normalizedTimeFormat);
            const getSafeDate = () => {
              const dateValue = value ? dayjs(value) : dayjs();
              return dateValue && dateValue.isValid() ? dateValue : dayjs();
            };
            const currentDate = getSafeDate();
            const currentHour24 = currentDate.hour();
            const currentMinute = currentDate.minute();
            const currentMinuteSnapped = Math.floor((currentMinute || 0) / 15) * 15;
            const currentHour12 = ((currentHour24 + 11) % 12) + 1;
            const currentAmPm = currentHour24 >= 12 ? 'PM' : 'AM';

            return (
          <Stack spacing={3}>
            <Box>
              {label && (
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  {label}
                </Typography>
              )}

              <DatePicker
                disablePast={others?.disablePast}
                value={value ? dayjs(value) : null}
                onChange={(newDate) => {
                  if (!newDate || !newDate.isValid()) {
                    onChange(null);
                    return;
                  }
                  const updated = newDate
                    .hour(Number.isInteger(currentHour24) ? currentHour24 : 9)
                    .minute(currentMinuteSnapped)
                    .second(0)
                    .millisecond(0);
                  onChange(updated);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    variant={variant || 'outlined'}
                    error={!!error}
                    helperText={error && error.message}
                  />
                )}
                {...others}
              />

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Time
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {/* Hour select */}
                  <FormControl fullWidth>
                    <InputLabel id={`${_schema}-hour-label`}>Hour</InputLabel>
                    <Select
                      labelId={`${_schema}-hour-label`}
                      label="Hour"
                      value={
                        use12HourFormat
                          ? String(currentHour12)
                          : String(currentHour24).padStart(2, '0')
                      }
                      onChange={(e) => {
                        const selectedHour = parseInt(e.target.value, 10);
                        const selectedHour24 = use12HourFormat
                          ? (selectedHour % 12) + (currentAmPm === 'PM' ? 12 : 0)
                          : selectedHour;
                        const updated = getSafeDate()
                          .hour(selectedHour24)
                          .minute(currentMinuteSnapped)
                          .second(0)
                          .millisecond(0);
                        onChange(updated);
                      }}
                    >
                      {Array.from({ length: use12HourFormat ? 12 : 24 }, (_, i) =>
                        use12HourFormat ? i + 1 : i
                      ).map((hourOption) => (
                        <MenuItem
                          key={hourOption}
                          value={
                            use12HourFormat
                              ? String(hourOption)
                              : String(hourOption).padStart(2, '0')
                          }
                        >
                          {use12HourFormat ? String(hourOption) : String(hourOption).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Typography variant="h6" component="span" sx={{ lineHeight: 1 }}>
                    :
                  </Typography>

                  {/* Minute select */}
                  <FormControl fullWidth>
                    <InputLabel id={`${_schema}-minute-label`}>Minute</InputLabel>
                    <Select
                      labelId={`${_schema}-minute-label`}
                      label="Minute"
                      value={String(currentMinuteSnapped).padStart(2, '0')}
                      onChange={(e) => {
                        const m = parseInt(e.target.value, 10);
                        const updated = getSafeDate()
                          .minute(m)
                          .second(0)
                          .millisecond(0);
                        onChange(updated);
                      }}
                    >
                      {[0, 15, 30, 45].map((mm) => (
                        <MenuItem key={mm} value={String(mm).padStart(2, '0')}>
                          {String(mm).padStart(2, '0')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {use12HourFormat && (
                    <FormControl fullWidth>
                      <InputLabel id={`${_schema}-ampm-label`}>AM/PM</InputLabel>
                      <Select
                        labelId={`${_schema}-ampm-label`}
                        label="AM/PM"
                        value={currentAmPm}
                        onChange={(e) => {
                          const selectedAmPm = e.target.value;
                          const selectedHour24 =
                            (currentHour12 % 12) + (selectedAmPm === 'PM' ? 12 : 0);
                          const updated = getSafeDate()
                            .hour(selectedHour24)
                            .minute(currentMinuteSnapped)
                            .second(0)
                            .millisecond(0);
                          onChange(updated);
                        }}
                      >
                        <MenuItem value="AM">AM</MenuItem>
                        <MenuItem value="PM">PM</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                </Box>

                <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                  Time slots available every 15 minutes
                </Typography>
              </Box>
            </Box>
          </Stack>
            );
          })()}
        </LocalizationProvider>
      )}
    />
  );
}
